import { useCallback, useMemo, useRef, useState } from 'react';
import { routePresets } from '../registries';

export interface SplineDriveOptions {
  preset: string;
  speed: number;
  /** Normalised stop positions 0..1 along the route (original order). */
  stopTs?: number[];
}

export interface SplinePose {
  x: number;
  z: number;
  yaw: number;
  t: number;
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function polylineLength(points: [number, number][]): number {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]);
  }
  return total;
}

export function samplePolyline(points: [number, number][], t: number): SplinePose {
  const clamped = Math.max(0, Math.min(1, t));
  if (points.length === 0) return { x: 0, z: 0, yaw: 0, t: clamped };
  if (points.length === 1) return { x: points[0][0], z: points[0][1], yaw: 0, t: clamped };

  const lengths: number[] = [0];
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    const dx = points[i][0] - points[i - 1][0];
    const dz = points[i][1] - points[i - 1][1];
    total += Math.hypot(dx, dz);
    lengths.push(total);
  }

  const target = clamped * total;
  let seg = 1;
  while (seg < lengths.length && lengths[seg] < target) seg += 1;
  const i1 = Math.min(seg, points.length - 1);
  const i0 = Math.max(0, i1 - 1);
  const segLen = lengths[i1] - lengths[i0] || 1;
  const localT = (target - lengths[i0]) / segLen;
  const x = lerp(points[i0][0], points[i1][0], localT);
  const z = lerp(points[i0][1], points[i1][1], localT);
  const yaw = Math.atan2(points[i1][1] - points[i0][1], points[i1][0] - points[i0][0]);
  return { x, z, yaw, t: clamped };
}

/**
 * Vehicle follows a route spline and pauses at stops by normalised t.
 * `arrivedStopIndex` is the index in the original `stopTs` array.
 */
export function useSplineDrive({ preset, speed, stopTs = [] }: SplineDriveOptions) {
  const route = useMemo(() => routePresets.get(preset), [preset]);
  const [pose, setPose] = useState<SplinePose>(() => samplePolyline(route.points, 0));
  const [paused, setPaused] = useState(false);
  const [arrivedStopIndex, setArrivedStopIndex] = useState<number | null>(null);
  const [finished, setFinished] = useState(false);

  const tRef = useRef(0);
  const nextStopRef = useRef(0);
  const pausedRef = useRef(false);
  const pointsRef = useRef(route.points);
  const stopTsRef = useRef(stopTs);
  const speedRef = useRef(speed);

  pointsRef.current = route.points;
  stopTsRef.current = stopTs;
  speedRef.current = speed;

  const reset = useCallback(() => {
    tRef.current = 0;
    nextStopRef.current = 0;
    pausedRef.current = false;
    setPaused(false);
    setArrivedStopIndex(null);
    setFinished(false);
    setPose(samplePolyline(pointsRef.current, 0));
  }, []);

  const resume = useCallback(() => {
    pausedRef.current = false;
    setPaused(false);
    setArrivedStopIndex(null);
  }, []);

  const advance = useCallback((dtSec: number) => {
    if (pausedRef.current) return;

    const points = pointsRef.current;
    const stops = stopTsRef.current;
    const spd = speedRef.current;
    const total = polylineLength(points);
    const deltaT = total > 0 ? (spd * dtSec) / total : 0;
    let nextT = Math.min(1, tRef.current + deltaT);

    const stopIndex = nextStopRef.current;
    if (stopIndex < stops.length) {
      const stopT = stops[stopIndex];
      if (tRef.current < stopT && nextT >= stopT) {
        nextT = stopT;
        tRef.current = nextT;
        nextStopRef.current = stopIndex + 1;
        pausedRef.current = true;
        setPaused(true);
        setArrivedStopIndex(stopIndex);
        setPose(samplePolyline(points, nextT));
        return;
      }
    }

    tRef.current = nextT;
    setPose(samplePolyline(points, nextT));
    if (nextT >= 1) setFinished(true);
  }, []);

  return {
    pose,
    paused,
    arrivedStopIndex,
    finished,
    advance,
    resume,
    reset,
    route,
  };
}
